/**
 * 카카오 애드핏(AdFit) 배너 광고.
 *
 * SPA/SSR 안전 래퍼:
 * - 광고 소재는 클라이언트에서만 로드(useEffect) — SSR HTML에는 빈 컨테이너만 나간다.
 * - kakao 의 `ba.min.js` 는 로드 시점에 페이지의 `ins.kakao_ad_area` 를 스캔해 광고를 채운다.
 *   SPA 라우팅으로 컴포넌트가 재마운트될 때마다 ins + script 를 새로 삽입해 재호출을 유도한다.
 * - React 가 직접 관리하지 않는 노드(우리가 appendChild 한 ins/script)만 다루고, 정리 시
 *   컨테이너 내부만 비운다 → React 언마운트와의 removeChild 경합을 피한다.
 */
import { useEffect, useRef } from 'react'

interface AdfitBannerProps {
  /** 애드핏 광고단위 ID (예: DAN-xxxxxxxx) — HTML 에 노출되는 공개값 */
  adUnit: string
  width: number
  height: number
  className?: string
}

export default function AdfitBanner({ adUnit, width, height, className }: AdfitBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const ins = document.createElement('ins')
    ins.className = 'kakao_ad_area'
    ins.style.display = 'none'
    ins.setAttribute('data-ad-unit', adUnit)
    ins.setAttribute('data-ad-width', String(width))
    ins.setAttribute('data-ad-height', String(height))

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = '//t1.kakaocdn.net/kas/static/ba.min.js'
    script.async = true

    container.appendChild(ins)
    container.appendChild(script)

    return () => {
      // 우리가 삽입한 노드만 제거(React 는 container 만 관리) → removeChild 경합 회피
      container.innerHTML = ''
    }
  }, [adUnit, width, height])

  // 광고 영역이 로드 전에도 레이아웃을 차지하도록 크기 예약(레이아웃 시프트 방지)
  return <div ref={containerRef} className={className} style={{ width, height }} />
}
